import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OrderStep, OrderTraceItem } from '../models/order-timeline.model';
import { environment } from '../../../../environments/environment';
import { firstValueFrom } from 'rxjs';

export interface OrderTimelinePayload {
  tenantId: string;
  orderId: string;
  orderType: string;
}

export interface TimelineStepsPayload {
  dispatchNumber: string;
  deliveryMode: string;
  traceInfo: OrderTraceItem;
}

@Injectable({ providedIn: 'root' })
export class OrderTimelineService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.API_ORDERS}`;

  async getOrderTimeline(payload: OrderTimelinePayload): Promise<OrderTraceItem[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<OrderTraceItem[]>(
          `${this.endpoint}/v2/${payload.tenantId}/orders/${payload.orderId}/trace-info`,
          {
            params: { orderType: payload.orderType },
          }
        )
      );
      return response || [];
    } catch (error) {
      return [];
    }
  }

  static parseTimelineSteps(payload: TimelineStepsPayload): {
    steps: OrderStep[];
    isCanceled: boolean;
  } {
    const { dispatchNumber, deliveryMode, traceInfo } = payload;

    let traces = [];

    if (!dispatchNumber || dispatchNumber.trim() === '') {
      traces = traceInfo.dispatches.flatMap(d => d.trace);
    } else {
      const dispatch = traceInfo.dispatches.find(
        (d) => d.dispatchNumber === dispatchNumber
      );

      if (!dispatch) {
        return { steps: [], isCanceled: false };
      }

      traces = dispatch.trace;
    }

    // Check if the FINAL state (most recent) is canceled, not if ANY trace was canceled
    const finalTrace = traces[traces.length - 1];
    const hasCanceled = finalTrace ? (
      finalTrace.technicalState?.toUpperCase() === 'CANCELLED' ||
      finalTrace.technicalState?.toUpperCase() === 'CANCELED'
    ) : false;

    const traceOrdered = [...traces].reverse();
    const steps = this.parseTraceSteps(traceOrdered, deliveryMode);

    return { steps, isCanceled: hasCanceled };
  }

  private static parseTraceSteps(trace: any[], deliveryMode: string): OrderStep[] {
    const usedStatuses = new Set<string>();
    const steps: OrderStep[] = [];

    for (const t of trace) {
      const key = t.technicalState?.toLowerCase();
      const state = t.technicalState?.toUpperCase();
      const isCanceled = state === 'CANCELLED' || state === 'CANCELED';

      const step: OrderStep = {
        status: t.technicalState ?? t.status,
        title: t.technicalStateDescription ?? t.status,
        badge: t.currentEntityName ?? t.currentEntityCode ?? undefined,
        date: t.currentStateDate ? new Date(t.currentStateDate) : undefined,
        stepState: isCanceled ? 'canceled' : 'completed',
      };

      if (key && !usedStatuses.has(step.status)) {
        usedStatuses.add(step.status);
        steps.push(step);
      }
    }

    return steps;
  }

  static applyBaseFlow(trace: any[], deliveryMode: string): OrderStep[] {
    const flowByDeliveryMode = environment.commons.flowByDeliveryMode;
    const usedStatuses = new Set<string>();
    const finalSteps: OrderStep[] = [];
    const baseFlow = flowByDeliveryMode[deliveryMode] ?? [];

    const baseMap = new Map(baseFlow.map((b) => [b.status.toLowerCase(), b]));

    for (const t of trace) {
      const key = t.technicalState?.toLowerCase();
      const base = key ? baseMap.get(key) : undefined;

      const step: OrderStep = {
        status: base?.status ?? t.technicalState ?? t.status,
        title: base?.title ?? t.technicalStateDescription ?? t.status,
        badge: t.currentEntityName ?? t.currentEntityCode ?? undefined,
        date: t.currentStateDate ? new Date(t.currentStateDate) : undefined,
        stepState: 'completed',
      };

      if (step.status && !usedStatuses.has(step.status)) {
        usedStatuses.add(step.status);
        finalSteps.push(step);
      }
    }

    for (let i = 0; i < baseFlow.length; i++) {
      const base = baseFlow[i];
      const exists = finalSteps.find(
        (s) => s.status.toLowerCase() === base.status.toLowerCase()
      );

      if (!exists) {
        const wasSkipped = baseFlow
          .slice(i + 1)
          .some((next) => usedStatuses.has(next.status));
        const step: OrderStep = {
          status: base.status,
          title: base.title,
          badge: null,
          stepState: wasSkipped ? 'completed' : 'pending',
        };

        const nextBase = baseFlow
          .slice(i + 1)
          .find((b) => finalSteps.find((f) => f.status === b.status));
        const insertAt = nextBase
          ? finalSteps.findIndex((f) => f.status === nextBase.status)
          : -1;

        if (insertAt !== -1) {
          finalSteps.splice(insertAt, 0, step);
        } else {
          finalSteps.push(step);
        }
      }
    }

    const hasDelivered = finalSteps.some(
      (s) => s.status === 'DELIVERED' && s.stepState === 'completed'
    );
    if (!hasDelivered) {
      const last = [...finalSteps]
        .reverse()
        .find((s) => s.stepState === 'completed');
      if (last) last.stepState = 'active';
    }
    const pickingExists = finalSteps.some(
      (s) => s.status === 'PENDING_PICKING'
    );
    if (!pickingExists) {
      finalSteps.unshift({
        status: 'PENDING_PICKING',
        title:
          baseFlow.find((f) => f.status === 'PENDING_PICKING')?.title ||
          'Pendiente de Picking',
        stepState: 'pending',
        badge: null,
      });
    }

    return finalSteps;
  }
}
